import React, { useEffect, useMemo, useState } from 'react';
import { useCollection } from 'functions/hooks/useCollection';
import { VehicleProps } from 'components/VehicleCard/VehicleCard';
import { CircularProgress } from '@mui/material';
import GlobalMapview from 'components/Mapview/GlobalMapview';
import { where } from 'firebase/firestore';
import MapPinAlert from 'assets/icons/map-pin-alert.svg';
import MapPinLocked from 'assets/icons/map-pin-locked.svg';
import MapPinUnlocked from 'assets/icons/map-pin-unlocked.svg';
import './FullGlobalMap.scss';
import { TamperType } from 'types/TamperType';

const chunkArray = <T,>(array: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
};

const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371e3;
  const φ1 = lat1 * Math.PI/180;
  const φ2 = lat2 * Math.PI/180;
  const Δφ = (lat2-lat1) * Math.PI/180;
  const Δλ = (lon2-lon1) * Math.PI/180;

  const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
          Math.cos(φ1) * Math.cos(φ2) *
          Math.sin(Δλ/2) * Math.sin(Δλ/2);
  
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
};

const LOCATION_UPDATE_THRESHOLD = 100;

type FilterType = 'locked' | 'unlocked' | 'tamper';

const FullGlobalMap: React.FC = () => {
  const [vehicles, setVehicles] = useState<VehicleProps[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeFilters, setActiveFilters] = useState<FilterType[]>([]);
  const vehiclesCollection = useCollection<any>('vehicles');
  const devicesCollection = useCollection<any>('devices');

  const filteredVehicles = useMemo(() => {
    if (activeFilters.length === 0) return vehicles;
    return vehicles.filter(vehicle =>
      activeFilters.some(filter => {
        switch (filter) {
          case 'locked':
            return vehicle.isLocked;
          case 'unlocked':
            return !vehicle.isLocked;
          case 'tamper':
            return vehicle.tamperActive;
          default:
            return false;
        }
      })
    );
  }, [vehicles, activeFilters]);

  const mapViewVehicles = useMemo(
    () =>
      filteredVehicles.map(vehicle => ({
        lat: vehicle.gps.lat,
        lng: vehicle.gps.lng,
        isLocked: vehicle.isLocked,
        tamperAlert: vehicle.tamperActive,
        vehicleInfo: { ...vehicle },
      })),
    [filteredVehicles]
  );

  useEffect(() => {
    let cancelled = false;
    const unsubscribes: Array<() => void> = [];

    (async () => {
      try {
        const vehicleData = await vehiclesCollection.queryMany([where('deviceId', '!=', null)]);
        if (cancelled) return;

        const deviceToVehicleMap = new Map(
          vehicleData.map(({ data, id }) => [data.deviceId, { ...data, vehicleId: id }])
        );

        const deviceIds = vehicleData.map(v => v.data.deviceId).filter(Boolean);
        if (deviceIds.length === 0) {
          setLoading(false);
          return;
        }

        const vehicleMap = new Map<string, VehicleProps>();

        for (const chunk of chunkArray(deviceIds, 30)) {
          if (cancelled) return;
          const unsubscribe = devicesCollection.queryManyStream(
            [where('__name__', 'in', chunk)],
            (devices) => {
              if (cancelled) return;
              let changed = false;
              devices.forEach(({ data: device, id: deviceId }) => {
                const vehicleData = deviceToVehicleMap.get(deviceId);
                if (!vehicleData) return;

                const newLat = device.LastLocation?.latLng?.latitudeDeg;
                const newLng = device.LastLocation?.latLng?.longitudeDeg;
                if (!newLat || !newLng) return;

                const currentVehicle = vehicleMap.get(deviceId);

                if (currentVehicle?.gps.lat && currentVehicle?.gps.lng) {
                  const distance = calculateDistance(
                    currentVehicle.gps.lat,
                    currentVehicle.gps.lng,
                    newLat,
                    newLng
                  );

                  if (distance < LOCATION_UPDATE_THRESHOLD) {
                    return;
                  }
                }

                vehicleMap.set(deviceId, {
                  ...vehicleData,
                  gps: { lat: newLat, lng: newLng },
                  deviceActive: device.active,
                  deviceSerialNumber: device.serialNumber,
                  isLocked: device.LockResultLog?.requestLock,
                  tamperActive: device?.TamperLog?.active === true && device?.TamperLog?.cancelled !== true && device?.TamperLog?.tamperType === TamperType.BATTERY,
                  isTrackerOnly: device.isTrackerOnly,
                  deviceId
                });
                changed = true;
              });

              // One setState per snapshot batch, not one per device.
              if (changed) {
                setVehicles(Array.from(vehicleMap.values()));
              }
            }
          );
          unsubscribes.push(unsubscribe);
        }

        if (!cancelled) setLoading(false);
      } catch (error) {
        if (cancelled) return;
        setError(error instanceof Error ? error.message : 'Failed to load vehicles');
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      unsubscribes.forEach(unsub => unsub());
    };
  }, [vehiclesCollection, devicesCollection]);

  if (error) {
    return <div className="global-map__error">Error: {error}</div>;
  }

  if (loading) {
    return (
      <div className='global-map__loader'>
        <CircularProgress size='4rem' color='inherit' />
      </div>
    );
  }

  return (
    <div className='global-map'>
      <div className="global-map__filters">
        <div className="filters-container">
          <button
            onClick={() => {
              setActiveFilters(prev => 
                prev.includes('locked') 
                  ? prev.filter(f => f !== 'locked')
                  : [...prev, 'locked']
              )
            }}
            className={`filter-button ${
              activeFilters.includes('locked') ? 'filter-button--active' : ''
            }`}
          >
            <img src={MapPinLocked} className="filter-icon" alt="Locked" />
            <span className="filter-label">Locked</span>
          </button>
          <button
            onClick={() => {
              setActiveFilters(prev => 
                prev.includes('unlocked') 
                  ? prev.filter(f => f !== 'unlocked')
                  : [...prev, 'unlocked']
              )
            }}
            className={`filter-button ${
              activeFilters.includes('unlocked') ? 'filter-button--active' : ''
            }`}
          >
            <img src={MapPinUnlocked} className="filter-icon" alt="Unlocked" />
            <span className="filter-label">Unlocked</span>
          </button>
          <button
            onClick={() => {
              setActiveFilters(prev => 
                prev.includes('tamper') 
                  ? prev.filter(f => f !== 'tamper')
                  : [...prev, 'tamper']
              )
            }}
            className={`filter-button ${
              activeFilters.includes('tamper') ? 'filter-button--active' : ''
            }`}
          >
            <img src={MapPinAlert} className="filter-icon" alt="Tamper Alert" />
            <span className="filter-label">Tamper Alert</span>
          </button>
        </div>
      </div>
      <GlobalMapview
        vehicles={mapViewVehicles}
        onMarkerClick={() => {}}
      />
    </div>
  );
};

export default FullGlobalMap;